import { isAxiosError } from 'axios';
import { createAction } from '@reduxjs/toolkit';
import accounting from 'accounting';
import { matchesProperty, partial } from 'lodash';
import { EMPTY, Observable, catchError, of, pipe } from 'rxjs';

import { IS_PRODUCTION } from './globals';
import errorReporter from './reporter'

import type { CaptureContext } from '@sentry/types'
import type { Action, ActionCreator, PayloadAction} from '@reduxjs/toolkit';
import type { RootState } from 'store/wos_store';

/**
 * Use in array.find calls to find something by its id property
 */
export const byId = partial(matchesProperty<string | number | undefined>, 'id');

/**
 * Prices for Cart API are formatted as cents, ie 1899 for $18.99
 * 
 * We need to format these for display, as well as marking $0.00 as Free
 */
export function formatPrice(price?: number, options?: accounting.CurrencySettings<any>, atCost?: boolean) {
  if (price === undefined) return '';
  if (atCost) return 'At Cost';
  if (price === 0) return 'FREE';

  return accounting.formatMoney(price / 100, {...options})
}

/** Get the payload of an action in a map */
export const pluckPayload = <T>({ payload }: PayloadAction<T>) => payload;

/**
 * Explicitly capture errors, and log to console if in dev/local
 */
export function logError(e: Error) {
  errorReporter.captureException(e);

  if (!IS_PRODUCTION && process.env.NODE_ENV !== 'test') {
    console.error(e);
  }
}

/** 
 * Slot this in where you'd use a catchError in an Epic
 * Can take the action that should be returned (like a .rejected() action)
 * or nothing which will just pass through an empty observable
 */
export function catchAndLogError<T extends Action>(a?: Action | ActionCreator<T>) {
  return pipe<Observable<Action>, Observable<Action>>(
    catchError((e: Error) => {
      let capture = true;
      let captured: Error = e;
      let context: CaptureContext | undefined = undefined;

      // Add some additional handling for network errors
      if (isAxiosError(e)) {
        const message = e.response?.data?.error || `Request failed: ${e.response?.statusText || 'No reason provided'}`
        if (e.response && e.response.status >= 500) {
          // Normally I don't think its worth reporting 500 errors, but for short time after deploying
          // cart refactor its helpful
          captured = new Error(message);
          captured.name = `NetworkError - ${e.response.status}`;
        } else if (e.response && e.response.status) {
          captured = new Error(message);
          captured.name = `NetworkError - ${e.response.status}`;
        }

        context = { extra: { response: e.response?.data, requestURL: `${e.config?.baseURL}/${e.config?.url}`, requestMethod: e.config?.method, requestData: e.config?.data } };
      }

        if (capture) {
          errorReporter.captureException(captured, context);
        }

        if (!IS_PRODUCTION && process.env.NODE_ENV !== 'test') {
          console.error(captured)
          console.error(context)
        }

      if (!a) return EMPTY;

      if (typeof a === 'function') {
        return of(a(e));
      }

      return of(a)
    })
  )
}

/**
 * Creates a structured set of actions for handling API requests
 * 
 * Actions are created with the name of the related domain, the name of the action,
 * and the status of the request.
 * 
 * Type arguments are for the type of payload for the Pending action and Fulfilled action -- 
 * essentially the argument types for request & response.
 * 
 * Default is void, for no payload in the action.
 */
export const createAPIActions = <P = void, F = void>(domain: keyof RootState, actionName: string)  => ({
  pending: createAction<P>(`${String(domain)}/${actionName}/pending`),
  fulfilled: createAction<F>(`${String(domain)}/${actionName}/fulfilled`),
  rejected: createAction(`${String(domain)}/${actionName}/rejected`),
})